import { useEffect, useRef, useState, type RefObject } from 'react';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { logger } from '@/utils/logger';

/** Finger travel (px) required before a refresh is triggered on release. */
const THRESHOLD = 70;
/** Cap on how far the content can be dragged down. */
const MAX_PULL = 120;
/** Ratio of finger travel translated into visible pull (adds rubber-band feel). */
const RESISTANCE = 0.5;
/** Where the spinner rests while the refresh is in flight. */
const SPINNER_REST = 60;

export interface PullToRefreshState {
  /** Attach to the scroll container that wraps the pullable content. */
  containerRef: RefObject<HTMLDivElement>;
  /** Current vertical offset (px) the content is translated by. */
  pull: number;
  /** True while `onRefresh` is running. */
  refreshing: boolean;
  /** True while the finger is actively dragging (transitions should be off). */
  dragging: boolean;
  /** 0..1 progress toward the trigger threshold (drives the pre-spin arc). */
  progress: number;
  /** False on web / non-native — consumers should render children unwrapped. */
  enabled: boolean;
}

/**
 * Pull-to-refresh gesture for the native app. Tracks a downward drag that begins
 * at the very top of the page (`window.scrollY <= 0`), takes over the native
 * scroll, and runs `onRefresh` when released past the threshold.
 *
 * Native-only: on web it returns `enabled: false` and no-op state so the wrapping
 * component can render its children untouched.
 */
export function usePullToRefresh(onRefresh: () => Promise<unknown> | void): PullToRefreshState {
  const enabled = Capacitor.isNativePlatform();
  const containerRef = useRef<HTMLDivElement>(null);

  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Refs mirror state for use inside the (once-attached) native event listeners.
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const draggingRef = useRef(false);
  const startYRef = useRef<number | null>(null);
  const hapticFiredRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const setPullValue = (value: number) => {
    pullRef.current = value;
    setPull(value);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return;

    const reset = () => {
      startYRef.current = null;
      if (draggingRef.current) {
        draggingRef.current = false;
        setDragging(false);
      }
      if (pullRef.current !== 0) setPullValue(0);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      // Only begin tracking a pull when the page is scrolled to the very top.
      if (window.scrollY > 0) {
        startYRef.current = null;
        return;
      }
      startYRef.current = e.touches[0].clientY;
      hapticFiredRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (refreshingRef.current || startYRef.current === null) return;
      // Scrolled away from the top mid-gesture — hand control back to native scroll.
      if (window.scrollY > 0) {
        reset();
        return;
      }

      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        // Upward / neutral movement — let the page scroll normally.
        if (draggingRef.current) reset();
        return;
      }

      // Downward pull at the top: take over the gesture.
      if (!draggingRef.current) {
        draggingRef.current = true;
        setDragging(true);
      }
      e.preventDefault();

      const next = Math.min(delta * RESISTANCE, MAX_PULL);
      setPullValue(next);

      if (next >= THRESHOLD && !hapticFiredRef.current) {
        hapticFiredRef.current = true;
        void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
      } else if (next < THRESHOLD) {
        hapticFiredRef.current = false;
      }
    };

    const onTouchEnd = () => {
      if (refreshingRef.current) return;
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      setDragging(false);
      startYRef.current = null;

      if (wasDragging && pullRef.current >= THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullValue(SPINNER_REST);
        Promise.resolve(onRefreshRef.current())
          .catch((err) => logger.error('Pull-to-refresh failed:', err))
          .finally(() => {
            refreshingRef.current = false;
            setRefreshing(false);
            setPullValue(0);
          });
      } else {
        setPullValue(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    // Non-passive so we can preventDefault the native scroll while pulling.
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled]);

  const progress = refreshing ? 1 : Math.min(pull / THRESHOLD, 1);

  return { containerRef, pull, refreshing, dragging, progress, enabled };
}
